package com.heelion.farmafreelance.agent

import com.heelion.farmafreelance.agent.RisponditoreDomande.creatoreTipo
import com.heelion.farmafreelance.models.AnnuncioTurno
import com.heelion.farmafreelance.models.Match
import com.heelion.farmafreelance.models.MatchStato
import com.heelion.farmafreelance.models.UserRole
import com.heelion.farmafreelance.models.isProfessionista
import com.heelion.farmafreelance.models.Professione
import com.heelion.farmafreelance.utils.TimeUtils
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.LocalTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.plus
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Duration.Companion.hours

/** Un promemoria da mostrare sul dispositivo all'istante [quando]. [id] è stabile per turno e tipo. */
data class PromemoriaTurno(val id: Int, val quando: Instant, val titolo: String, val testo: String)

/**
 * Farmy decide da solo quando ricordarti i turni, secondo i promemoria che hai attivato
 * ([PreferenzePromemoria]), senza server né cron: il dispositivo li mostra all'ora giusta anche
 * ad app chiusa (su Android, vedi PromemoriaLocali). Ricalcolati a ogni apertura dell'app.
 *
 * - [TipoPromemoria.TURNO_IMMINENTE]: la sera prima alle [ORA_SERA_PRIMA] e [ORE_PRIMA] ore prima.
 * - [TipoPromemoria.TURNI_GIORNATA]: all'ora scelta, solo nei giorni con turni (prossimi [GIORNI]).
 * - [TipoPromemoria.DA_CONFERMARE] / [TipoPromemoria.IN_ATTESA]: una volta, alla prossima
 *   occorrenza dell'ora scelta, solo se ce ne sono (niente raffiche se l'app resta chiusa).
 * - [TipoPromemoria.DISDETTE]: non qui, è una notifica push del server in tempo reale.
 *
 * L'ora di inizio si calcola con [TimeUtils.combineDateAndTime], come nel resto dell'app.
 */
object PianificatorePromemoria {
    const val ORA_SERA_PRIMA = 18
    const val ORE_PRIMA = 2
    const val GIORNI = 14

    /**
     * @param dettagli annunci dei turni confermati (per ora di inizio), per id annuncio.
     */
    fun calcola(
        ruolo: UserRole?,
        matches: List<Match>,
        dettagli: Map<String, AnnuncioTurno>,
        adesso: Instant,
        tz: TimeZone = TimeZone.currentSystemDefault(),
        preferenze: PreferenzePromemoria = PreferenzePromemoria()
    ): List<PromemoriaTurno> {
        if (ruolo == null) return emptyList()
        val mio = ruolo.creatoreTipo() ?: return emptyList()
        val oggi = adesso.toLocalDateTime(tz).date

        // Turni confermati futuri con la loro ora di inizio.
        val confermati = matches.filter { it.stato == MatchStato.CONFERMATO }.mapNotNull { m ->
            val a = dettagli[m.annuncioId] ?: return@mapNotNull null
            val inizio = TimeUtils.combineDateAndTime(a.dataTurno, a.oraInizio)
            if (inizio <= adesso) null else Triple(m, a, inizio)
        }.sortedBy { it.third }

        val out = mutableListOf<PromemoriaTurno>()

        if (preferenze.attivo(TipoPromemoria.TURNO_IMMINENTE)) {
            for ((m, a, inizio) in confermati) {
                val chiDove = descrizione(ruolo, m)
                val chiave = m.id ?: m.annuncioId
                val seraPrima = LocalDateTime(inizio.toLocalDateTime(tz).date.minus(1, DateTimeUnit.DAY), LocalTime(ORA_SERA_PRIMA, 0)).toInstant(tz)
                if (seraPrima > adesso) {
                    out += PromemoriaTurno("$chiave-sera".hashCode(), seraPrima, "Domani hai un turno", "Domani alle ${a.oraInizio} $chiDove.")
                }
                val pocoPrima = inizio - ORE_PRIMA.hours
                if (pocoPrima > adesso) {
                    out += PromemoriaTurno("$chiave-prima".hashCode(), pocoPrima, "Turno tra $ORE_PRIMA ore", "Oggi alle ${a.oraInizio} $chiDove.")
                }
            }
        }

        if (preferenze.attivo(TipoPromemoria.TURNI_GIORNATA)) {
            confermati
                .groupBy { it.third.toLocalDateTime(tz).date }
                .filterKeys { it.toEpochDays() - oggi.toEpochDays() in 0 until GIORNI }
                .forEach { (giorno, turni) ->
                    val quando = LocalDateTime(giorno, preferenze.oraGiornata).toInstant(tz)
                    if (quando <= adesso) return@forEach
                    val elenco = turni.joinToString("; ") { (m, a, _) -> "${a.oraInizio} ${nomeControparte(ruolo, m)}" }
                    val titolo = if (turni.size == 1) "Oggi hai un turno" else "Oggi hai ${turni.size} turni"
                    out += PromemoriaTurno("giornata-$giorno".hashCode(), quando, titolo, "$elenco.")
                }
        }

        val prossimaOraSospesi = prossima(preferenze.oraSospesi, oggi, adesso, tz)
        if (preferenze.attivo(TipoPromemoria.DA_CONFERMARE)) {
            val daConfermare = matches.filter { it.stato == MatchStato.ACCETTATO && it.propostoDa == mio }
            if (daConfermare.isNotEmpty()) {
                out += PromemoriaTurno(
                    "da-confermare".hashCode(), prossimaOraSospesi,
                    if (daConfermare.size == 1) "Hai un turno da confermare" else "Hai ${daConfermare.size} turni da confermare",
                    elencoSospesi(ruolo, daConfermare, tz) + " Aprimi per confermare."
                )
            }
        }
        if (preferenze.attivo(TipoPromemoria.IN_ATTESA)) {
            val inAttesa = matches.filter {
                (it.stato == MatchStato.PROPOSTO && it.propostoDa == mio) || (it.stato == MatchStato.ACCETTATO && it.propostoDa != mio)
            }
            if (inAttesa.isNotEmpty()) {
                out += PromemoriaTurno(
                    "in-attesa".hashCode(), prossimaOraSospesi,
                    if (inAttesa.size == 1) "Un turno è in attesa di risposta" else "${inAttesa.size} turni in attesa di risposta",
                    elencoSospesi(ruolo, inAttesa, tz)
                )
            }
        }
        return out.sortedBy { it.quando }
    }

    /** Oggi all'ora data se è ancora nel futuro, altrimenti domani. */
    private fun prossima(ora: LocalTime, oggi: LocalDate, adesso: Instant, tz: TimeZone): Instant {
        val oggiAllOra = LocalDateTime(oggi, ora).toInstant(tz)
        return if (oggiAllOra > adesso) oggiAllOra else LocalDateTime(oggi.plus(1, DateTimeUnit.DAY), ora).toInstant(tz)
    }

    /** "Farmacia Rossi (26/9), Farmacia Bianchi (3/10)" — max 3. */
    private fun elencoSospesi(ruolo: UserRole, lista: List<Match>, tz: TimeZone): String {
        val primi = lista.sortedBy { it.dataTurno }.take(3).joinToString(", ") { m ->
            val data = m.dataTurno?.toLocalDateTime(tz)?.date?.let { " (${it.dayOfMonth}/${it.monthNumber})" } ?: ""
            nomeControparte(ruolo, m) + data
        }
        return primi + if (lista.size > 3) " e altri ${lista.size - 3}." else "."
    }

    private fun nomeControparte(ruolo: UserRole, m: Match): String =
        m.nomeControparte?.takeIf { it.isNotBlank() } ?: if (ruolo.isProfessionista) "la farmacia" else chiArriva(m)

    /** "hai il turno presso Farmacia Rossi, Via Roma 12, Milano" / "arriva Mario Bianchi per il turno". */
    private fun descrizione(ruolo: UserRole, m: Match): String = if (ruolo.isProfessionista) {
        val dove = listOfNotNull(m.nomeControparte, m.indirizzoControparte, m.cittaControparte)
            .filter { it.isNotBlank() }.joinToString(", ")
        "hai il turno" + if (dove.isNotEmpty()) " presso $dove" else ""
    } else {
        "arriva ${m.nomeControparte?.takeIf { it.isNotBlank() } ?: chiArriva(m)} per il turno"
    }

    /** Chi arriva in farmacia quando il nome non c'è: dipende dalla professione del match. */
    private fun chiArriva(m: Match) = if (m.professione == Professione.INFERMIERE) "l'infermiere" else "il farmacista"

}
